import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'preact/hooks';

import { Button } from '@/components/Buttons';
import { getProductsQueryOptions } from '@/queries/products';

import ProductEligibilityResult from './ProductEligibilityResult';
import { ProductFeaturesList, ProductFeaturesListItem } from './ProductFeatureList';
import ProductPrice from './ProductPrice';
import useCheckoutOrUpgrade from './useCheckoutOrUpgrade';
import { parseBenefits } from './utils';

interface EducatorWeekPassProps {
  entryPoint?: string; // for analytics only
}

const EducatorWeekPass = ({ entryPoint }: EducatorWeekPassProps) => {
  const { data: product } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: 'educator-week-pass',
    }),
    select: (data) => data.list[0],
  });

  const benefits = useMemo(() => parseBenefits(product), [product]);

  // Week pass is single-use (never tier-changeable), so this resolves to a plain checkout.
  const { disabled, loading, onClick } = useCheckoutOrUpgrade(product, {
    entryPoint,
    successPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
    cancelPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });

  return (
    <div
      className="flex w-full max-w-[336px] flex-col items-center justify-center rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]"
      data-component="educator-week-pass"
    >
      <div className="mt-3 flex w-full flex-col items-center gap-4">
        <div className="w-full px-4">
          <h4 className="preflight preflight-h4 text-2xl/7 font-bold text-green-900">Lesson Pack Week Pass</h4>
          <p className="preflight preflight-p text-sm text-stone-500">One-week access to zero-prep lesson pack</p>
        </div>
        <div className="flex w-full items-center justify-between gap-2 px-4">
          <ProductPrice currency={product?.price.currency} price={product?.price.price} />
        </div>
        <ProductFeaturesList className="w-full px-4">
          {benefits.map((benefit, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ProductFeaturesListItem key={index.toString()}>{benefit}</ProductFeaturesListItem>
          ))}
        </ProductFeaturesList>
      </div>
      <div className="w-full p-4">
        <div className="px-4">
          <Button
            data-component="checkout-button"
            disabled={disabled}
            loading={loading}
            size="lg"
            className="w-full"
            onClick={onClick}
          >
            Select Pass
          </Button>
          {product && <ProductEligibilityResult result={product.eligibility} className="mt-3" />}
        </div>
      </div>
    </div>
  );
};

export default EducatorWeekPass;
